<template>
  <WithAsideCards
    :data="data?.fields?.asides"
    data-marketing-material-v="3.1.3"
  >
    <div :class="$style.marketingMaterial">
      <div :class="$style.filtersBar">
        <div :class="$style.dataControls">
          <div :class="$style.filters">
            <BaseSelect
              v-model="selectedBrandFilter"
              :class="$style.select"
              :options="computedBrands"
              :active-filter-value="computedActiveBrandFilter"
              @on-change="() => (hashPriority = false)"
            />
            <BaseSelect
              v-model="selectedProductFilter"
              :class="$style.select"
              :placeholder="localizedStrings['Product'] || 'Product'"
              :options="computedProducts"
              :clearable="true"
              @on-change="() => (hashPriority = false)"
            />
            <SearchField
              :class="$style.search"
              :input-class="$style.searchInput"
              :placeholder="`${localizedStrings['search']} ${activeBrandFilter?.fields?.label}`"
              @request-query="(val) => (searchValue = val)"
              @clear-query="(val) => (searchValue = val)"
            />
          </div>
        </div>
      </div>
      <div :class="$style.marketingSetsWrapper">
        <div v-if="!computedRenderedEntries.length" :class="$style.noResults">
          {{ localizedStrings['no-results'] }}
        </div>
        <div v-else :class="$style.marketingSets">
          <MarketingSet
            v-for="(set, index) in computedRenderedEntries"
            :id="set?.fields?.id"
            :key="set?.sys?.id"
            :data-id="set?.sys?.id"
            :data="set"
            :place="index + 1"
            :search-query="searchValue"
          />
          <div v-if="computedHasMoreToRender" :class="$style.loadMoreWrapper">
            <BaseButton @click="onRequestMore">
              {{ localizedStrings['load-more'] }}
            </BaseButton>
          </div>
        </div>
      </div>
    </div>
  </WithAsideCards>
</template>

<script setup lang="ts">
import type {
  TypeEntriesCollectionWithoutUnresolvableLinksResponse,
  TypeMarketingMaterialSetSkeleton,
} from '~/types/contentful'

const { locale } = useI18n()
const { $client, $visibility, $userAccessCheck } = useNuxtApp()
const { localizedStrings } = useLocalizedStrings()
const { highestUserLevel } = storeToRefs(useUserStore())

const LOAD_COUNT = 10

// Props
const props = defineProps<{
  data: TypeEntriesCollectionWithoutUnresolvableLinksResponse
}>()

// Data
const activeBrandFilter = ref(props?.data?.fields?.filterOptions?.[0])
const selectedBrandFilter = ref(activeBrandFilter?.value?.fields?.value)
const selectedProductFilter = ref('')
const renderedNum = ref(LOAD_COUNT)
const searchValue = ref('')
const hashPriority = ref(false)

const getEntries = async (skip = 0) =>
  await $client.withoutUnresolvableLinks.getEntries<TypeMarketingMaterialSetSkeleton>(
    {
      content_type: 'marketingMaterialSet',
      include: 10,
      locale: locale.value,
      order: ['-fields.date'],
      skip,
    },
  )

// Async Data
const { data: fetchedData } = await useAsyncData(
  'marketingMaterial',
  async () => {
    const entries = []
    const response = await getEntries()
    entries.push(...(response?.items || []))

    while (entries?.length < response?.total) {
      const response = await getEntries(entries?.length)
      entries.push(...(response?.items || []))
    }

    // Get all products for filtering
    const uniqueProducts = entries.reduce<{ [key: string]: string }>(
      (accu, curr) => {
        const productTypes = curr.fields?.availableProductTypes
        if (productTypes) {
          productTypes.forEach((productType) => {
            const value = productType?.fields?.value
            const label = productType?.fields?.label
            if (value && label) accu[value] = label
          })
        }
        return accu
      },
      {},
    )
    const products = Object.entries(uniqueProducts).map(([key, value]) => ({
      label: value,
      value: key,
    }))

    return { entries, products }
  },
  {
    transform(input) {
      return {
        entries: input.entries,
        products: input.products,
        fetchedAt: useUnixTimestamp(),
      }
    },
    getCachedData: (key) => useCachedData(key),
  },
)

// Watch
watch(activeBrandFilter, async () => {
  // Avoid race condition of setting renderedNum if anchor tagging is active (on first change)
  if (!hashPriority.value) {
    renderedNum.value = LOAD_COUNT
  }

  // Keep selected category filter on desktop/mobile in sync
  if (activeBrandFilter?.value?.fields?.value) {
    selectedBrandFilter.value = activeBrandFilter?.value?.fields?.value
  }
})
watch(selectedBrandFilter, async () => {
  // Avoid race condition of setting renderedNum if anchor tagging is active (on first change)
  if (!hashPriority.value) {
    renderedNum.value = LOAD_COUNT
  }

  // Keep selected category filter on desktop/mobile in sync
  const matchedFilter = props?.data?.fields?.filterOptions?.find(
    (tag) => tag?.fields?.value === selectedBrandFilter.value,
  )
  if (matchedFilter) activeBrandFilter.value = matchedFilter
})
watch(searchValue, async () => (renderedNum.value = LOAD_COUNT))

// Computed
const computedAccessibleEntries = computed(() => {
  if (!fetchedData?.value?.entries?.length) return []

  return fetchedData.value.entries.filter((entry) => {
    const isVisible = $visibility(entry?.fields?.visibility)
    const hasAccess = $userAccessCheck(
      entry?.fields?.accessLevel,
      highestUserLevel.value,
    )
    return isVisible && hasAccess
  })
})

const computedBrands = computed(() => {
  if (!props?.data?.fields?.filterOptions) return []

  return props.data.fields.filterOptions.map((tag) => {
    return {
      label: tag?.fields?.label || '',
      value: tag?.fields?.value || '',
    }
  })
})

const computedActiveBrandFilter = computed(
  () =>
    computedBrands.value.find(
      (tag) => tag.value === activeBrandFilter.value?.fields?.value,
    )?.value || '',
)

const computedProducts = computed(() => {
  if (!fetchedData.value?.products) return []

  return [...fetchedData.value.products].sort((a, b) =>
    a.label.localeCompare(b.label),
  )
})

const getPreferredBrandValue = (
  entry: (typeof computedAccessibleEntries.value)[number] | undefined,
) => {
  return (
    entry?.fields?.associatedBrand?.fields?.value ||
    entry?.fields?.brand2?.fields?.value ||
    ''
  )
}

const getLegacyBrandLabel = (
  entry: (typeof computedAccessibleEntries.value)[number] | undefined,
) => {
  return entry?.fields?.brand || ''
}

const computedFilteredEntries = computed(() => {
  if (!computedAccessibleEntries?.value?.length) return []
  let filtered = computedAccessibleEntries?.value?.filter((entry) => {
    const preferredBrandValue = getPreferredBrandValue(entry)
    const legacyBrandLabel = getLegacyBrandLabel(entry)
    const hasBrand = preferredBrandValue || legacyBrandLabel

    if (!hasBrand) return false

    if (preferredBrandValue) {
      return preferredBrandValue === activeBrandFilter?.value?.fields?.value
    }

    return legacyBrandLabel === activeBrandFilter?.value?.fields?.label
  })
  if (searchValue?.value) {
    filtered = filtered.filter(
      (entry) =>
        entry?.fields?.assets?.filter((asset) =>
          asset?.fields?.title
            ?.toLowerCase?.()
            ?.includes?.(searchValue?.value?.toLowerCase?.()),
        ).length,
    )
  }
  if (selectedProductFilter.value) {
    filtered = filtered.filter((entry) => {
      return entry?.fields?.availableProductTypes
        ?.map((productType) => productType?.fields?.value)
        ?.includes(selectedProductFilter.value)
    })
  }
  return filtered
})

const computedRenderedEntries = computed(() => {
  if (!computedFilteredEntries?.value?.length) return []
  return computedFilteredEntries?.value?.slice(0, renderedNum?.value)
})

const computedHasMoreToRender = computed(
  () =>
    computedRenderedEntries?.value?.length <
    computedFilteredEntries?.value?.length,
)

// Methods
const onRequestMore = () => (renderedNum.value += LOAD_COUNT)

const onHashChange = () => {
  const activeId = window?.location?.hash?.substring(1)
  if (!activeId) return

  // Set priority for anchor tagging to decide value for renderedNum (on first change)
  hashPriority.value = true

  // Check if active set is rendered
  const renderedIndex = computedRenderedEntries.value?.findIndex(
    (entry) => entry?.fields?.id === activeId,
  )
  // If already rendered, no further action is needed
  if (renderedIndex !== -1) return

  // Check if active set is accessible
  const accessibleIndex = computedAccessibleEntries.value?.findIndex(
    (entry) => entry?.fields?.id === activeId,
  )
  // If accessible, switch to the respective brand filter
  if (accessibleIndex !== -1) {
    const entry = computedAccessibleEntries.value?.[accessibleIndex]
    const preferredBrandValue = getPreferredBrandValue(entry)
    const legacyBrandLabel = getLegacyBrandLabel(entry)

    const matchedFilter = props?.data?.fields?.filterOptions?.find(
      (tag) =>
        tag?.fields?.value === preferredBrandValue ||
        tag?.fields?.label === legacyBrandLabel,
    )
    if (matchedFilter) {
      activeBrandFilter.value = matchedFilter
    }
  }

  // Check if active set is filtered
  const filteredIndex = computedFilteredEntries.value?.findIndex(
    (entry) => entry?.fields?.id === activeId,
  )
  // If filtered, load untile active set is rendered
  if (filteredIndex !== -1) {
    renderedNum.value = filteredIndex + 1
  }
}

onMounted(() => {
  onHashChange()
  window.addEventListener('hashchange', onHashChange)
})

onBeforeUnmount(() => {
  window.removeEventListener('hashchange', onHashChange)
})
</script>

<style lang="scss" module>
.marketingMaterial {
  background-color: var(--gray-1);
}

.filtersBar {
  background-color: var(--white);
  box-shadow: 0 1px 3px var(--gray-4);
  padding: 10px 20px;
  margin-bottom: 20px;
}

.dataControls {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin: auto;

  @include media('md') {
    gap: 10px;
  }
}

.brandFilters {
  display: none;

  @include media('md') {
    display: block;
  }
}

.mobileBrandFilters {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  margin-bottom: 10px;

  @include media('md') {
    display: none;
  }
}

.filters {
  display: flex;
  margin-block: 20px;
  gap: 10px;
  flex-wrap: wrap;
  width: 100%;
}

.searchInput {
  text-overflow: ellipsis;
}

.select,
.search {
  width: 100%;
  min-width: 250px;

  @include media('sm') {
    width: auto;
  }
}

.search {
  @include media('sm') {
    min-width: 140px;
    flex: 1;
  }
}

.noResults {
  text-align: center;
  margin-bottom: 20px;
}

.marketingSetsWrapper {
  @include media('sm') {
    margin: 0 auto;
    max-width: 1350px;
  }
}

.marketingSets {
  background-color: var(--white);
  box-shadow: 0 4px 13px rgba(#000, 0.06);
}

.loadMoreWrapper {
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 30px 0;
  position: relative;

  &:before,
  &:after {
    content: '';
    background-color: var(--black-alpha10);
    height: 1px;
    position: absolute;
    width: calc(41% - 75px);
  }

  &:before {
    left: 6%;
  }

  &:after {
    right: 6%;
  }
}
</style>
